import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Sparkles, Trash2, Loader2, FileDown, Plus, X, Save, ArrowLeft } from "lucide-react";
import api from "../../lib/api";

const TOPICS = [
  { key: "identifikation", label: "Erkennung & Bestimmung" },
  { key: "oekologie", label: "Ökologie & Bestäuber" },
  { key: "heilwissen", label: "Traditionelles Heilwissen" },
  { key: "kueche", label: "Küche & Rezepte" },
  { key: "historie", label: "Kulturgeschichte" },
  { key: "sicherheit", label: "Sicherheit & Verwechslungen" },
  { key: "nachhaltigkeit", label: "Nachhaltiges Sammeln" },
  { key: "anekdote", label: "Anekdoten & Wusstest-du-Fakten" },
];

export default function TourScriptsAdmin() {
  const [scripts, setScripts] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([api.get("/admin/tour-scripts"), api.get("/plants")]);
      setScripts(s.data || []);
      setPlants((p.data || []).slice().sort((a, b) => (a.name_de || "").localeCompare(b.name_de || "")));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Tour-Scripts nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openScript = async (id) => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/tour-scripts/${id}`);
      setActive(r.data);
      setActiveId(id);
    } catch (e) {
      toast.error("Konnte Skript nicht laden");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Skript wirklich löschen?")) return;
    await api.delete(`/admin/tour-scripts/${id}`);
    toast.success("Gelöscht");
    if (activeId === id) { setActive(null); setActiveId(null); }
    await load();
  };

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.put(`/admin/tour-scripts/${activeId}`, {
        title: active.title, content_md: active.content_md,
      });
      toast.success("Gespeichert");
      await load();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!activeId) return;
    try {
      const r = await api.get(`/admin/tour-scripts/${activeId}/pdf?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `script-${(active.title || "tour").toLowerCase().replace(/\s+/g, "-").slice(0, 40)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("PDF-Download fehlgeschlagen");
    }
  };

  // ------- Detail view -------
  if (active && activeId) {
    return (
      <div className="space-y-4" data-testid="tour-script-detail">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <button
            onClick={() => { setActive(null); setActiveId(null); }}
            className="btn-secondary text-sm"
            data-testid="tour-script-back"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="tour-script-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
            <button onClick={downloadPdf} className="btn-secondary text-sm" data-testid="tour-script-download-pdf">
              <FileDown className="w-4 h-4" /> PDF
            </button>
            <button onClick={() => remove(activeId)} className="btn-secondary text-sm text-red-600" data-testid="tour-script-delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <input
          value={active.title || ""}
          onChange={(e) => setActive({ ...active, title: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border text-xl font-display"
          style={{ borderColor: "var(--wp-border)" }}
          data-testid="tour-script-title"
        />

        <div className="text-xs opacity-60">
          {active.plant_ids?.length || 0} Pflanzen · {active.duration_min || "?"} min · {active.audience} · {active.tone} · Sprache: {active.lang?.toUpperCase()}
        </div>

        {active.status === "generating" ? (
          <div className="p-6 rounded-2xl bg-[var(--wp-cream)] text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            <div>Skript wird gerade von Claude erzeugt … (bitte kurz warten und neu laden)</div>
          </div>
        ) : (
          <textarea
            value={active.content_md || ""}
            onChange={(e) => setActive({ ...active, content_md: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border font-mono text-sm"
            style={{ borderColor: "var(--wp-border)", minHeight: "60vh" }}
            data-testid="tour-script-editor"
          />
        )}
        <div className="text-xs opacity-50">
          Tipp: Bearbeite frei in Markdown (# Überschrift, ## Pflanze, * Bullet, **fett**, *kursiv*, {'>'} Zitat). Danach speichern und PDF exportieren.
        </div>
      </div>
    );
  }

  // ------- List view -------
  return (
    <div className="space-y-4" data-testid="tour-scripts-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">Tour-Skripte</h2>
          <p className="text-sm opacity-70">Erzeuge personalisierte Redetexte für deine geführten Wanderungen — mit Auswahl an Pflanzen und Themen.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="tour-script-new">
          <Plus className="w-4 h-4" /> Neues Skript
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
        </div>
      ) : scripts.length === 0 ? (
        <div className="p-8 rounded-2xl bg-[var(--wp-cream)] text-center text-sm opacity-70">
          Noch keine Skripte. Klicke oben rechts auf {'"'}Neues Skript{'"'}.
        </div>
      ) : (
        <div className="space-y-2">
          {scripts.map((s) => (
            <div
              key={s.id}
              className="plant-card p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 sm:gap-4 items-center hover:shadow-md cursor-pointer"
              onClick={() => openScript(s.id)}
              data-testid={`tour-script-row-${s.id}`}
            >
              <div>
                <div className="font-display text-lg flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: "var(--wp-olive)" }} />
                  {s.title || "(kein Titel)"}
                </div>
                <div className="text-xs opacity-60 mt-0.5">
                  {(s.plant_ids || []).length} Pflanzen · {s.duration_min || "?"} min · {s.audience} · {s.tone} · {s.lang?.toUpperCase()}
                </div>
              </div>
              <div className="text-xs opacity-60">
                {s.status === "generating" ? (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> in Erzeugung
                  </span>
                ) : (
                  new Date(s.updated_at || s.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
                )}
              </div>
              <button
                onClick={(ev) => { ev.stopPropagation(); remove(s.id); }}
                className="btn-secondary text-xs !py-1.5 !px-2"
                data-testid={`tour-script-delete-${s.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewScriptDialog
          plants={plants}
          onClose={() => setShowNew(false)}
          onCreated={async (scriptId) => {
            setShowNew(false);
            await load();
            openScript(scriptId);
          }}
        />
      )}
    </div>
  );
}

function NewScriptDialog({ plants, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [topics, setTopics] = useState(["identifikation", "kueche", "historie"]);
  const [duration, setDuration] = useState(90);
  const [audience, setAudience] = useState("erwachsene");
  const [tone, setTone] = useState("locker");
  const [lang, setLang] = useState("de");
  const [starting, setStarting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");

  const toggleTopic = (t) => setTopics((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const togglePlant = (pid) => setSelectedIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));

  const submit = async () => {
    if (selectedIds.length === 0) return toast.error("Mindestens eine Pflanze auswählen");
    setStarting(true); setMessage("Starte Job …"); setProgress(0);
    try {
      const r = await api.post("/admin/tour-scripts/generate-async", {
        title, plant_ids: selectedIds, topics, duration_min: duration, audience, tone, lang,
      });
      const jobId = r.data?.job_id;
      const scriptId = r.data?.script_id;
      const startedAt = Date.now();
      const maxMs = 5 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((res) => setTimeout(res, 3000));
        const j = await api.get(`/admin/jobs/${jobId}`);
        const st = j.data || {};
        setProgress(st.progress || 0);
        setMessage(st.message || "läuft …");
        if (st.status === "done") {
          toast.success(`Skript fertig (${st.result?.word_count || "?"} Wörter)`);
          onCreated(scriptId);
          return;
        }
        if (st.status === "error") throw new Error(st.error || "Job-Fehler");
      }
      throw new Error("Timeout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Job fehlgeschlagen");
    } finally {
      setStarting(false);
    }
  };

  const durationHint = useMemo(() => {
    const perPlant = 5;
    const suggested = Math.max(3, Math.round((duration - 15) / perPlant));
    return `≈ ${suggested} Pflanzen für ${duration} min`;
  }, [duration]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(26,47,28,0.6)" }}
      onClick={onClose}
      data-testid="tour-script-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: "var(--wp-olive)" }} />
            <h3 className="font-display text-xl">Neues Tour-Skript</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--wp-cream)]" data-testid="tour-script-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="overline block mb-1">Titel</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Frühlings-Kräuter im Grünwald"
              className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }}
              data-testid="tour-script-form-title"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label>
              <div className="overline mb-1">Dauer (min)</div>
              <input type="number" min="15" max="240" step="15" value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value || "90"))}
                className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }}
                data-testid="tour-script-form-duration"
              />
              <div className="text-[10px] opacity-60 mt-1">{durationHint}</div>
            </label>
            <label>
              <div className="overline mb-1">Zielgruppe</div>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full px-3 py-2 rounded-lg border bg-white"
                style={{ borderColor: "var(--wp-border)" }} data-testid="tour-script-form-audience"
              >
                <option value="familien">Familien mit Kindern</option>
                <option value="erwachsene">Erwachsene</option>
                <option value="fortgeschrittene">Fortgeschrittene</option>
              </select>
            </label>
            <label>
              <div className="overline mb-1">Tonfall</div>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full px-3 py-2 rounded-lg border bg-white"
                style={{ borderColor: "var(--wp-border)" }} data-testid="tour-script-form-tone"
              >
                <option value="locker">Locker & humorvoll</option>
                <option value="sachlich">Sachlich</option>
                <option value="poetisch">Poetisch</option>
              </select>
            </label>
            <label>
              <div className="overline mb-1">Sprache</div>
              <select value={lang} onChange={(e) => setLang(e.target.value)} className="w-full px-3 py-2 rounded-lg border bg-white"
                style={{ borderColor: "var(--wp-border)" }} data-testid="tour-script-form-lang"
              >
                <option value="de">Deutsch</option>
                <option value="fr">Französisch</option>
                <option value="lu">Luxemburgisch</option>
                <option value="en">Englisch</option>
              </select>
            </label>
          </div>

          <div>
            <div className="overline mb-2">Themen ({topics.length} ausgewählt)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {TOPICS.map((t) => (
                <label key={t.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={topics.includes(t.key)} onChange={() => toggleTopic(t.key)}
                    data-testid={`tour-script-topic-${t.key}`} />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Pflanzen ({selectedIds.length} ausgewählt)</div>
              <div className="flex gap-2 text-xs">
                <button className="underline" onClick={() => setSelectedIds(plants.map((p) => p.id))}>alle</button>
                <button className="underline" onClick={() => setSelectedIds([])}>keine</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }}>
              {plants.map((p) => {
                const selected = selectedIds.includes(p.id);
                return (
                  <button key={p.id} onClick={() => togglePlant(p.id)}
                    className={`flex items-center gap-2 p-1.5 rounded-lg text-left text-sm ${selected ? "bg-[var(--wp-primary)] text-white" : "bg-white hover:bg-[var(--wp-cream)]"}`}
                    style={{ border: `1px solid ${selected ? "var(--wp-primary)" : "var(--wp-border)"}` }}
                    data-testid={`tour-script-plant-${p.slug}`}
                  >
                    {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                    <span className="flex-1 truncate">{p.name_de}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex items-center gap-3" style={{ borderColor: "var(--wp-border)" }}>
          {starting ? (
            <div className="flex-1 flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              <div className="flex-1">
                <div className="text-xs opacity-70">{message}</div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-[var(--wp-primary)] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div className="text-xs">{progress}%</div>
            </div>
          ) : (
            <>
              <button onClick={onClose} className="btn-secondary text-sm ml-auto">Abbrechen</button>
              <button onClick={submit} className="btn-primary" data-testid="tour-script-form-submit">
                <Sparkles className="w-4 h-4" /> Skript erzeugen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
